import { useState } from "react";
import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * AboutValues — the four core values, presented as flippable cards.
 *
 * Interaction: hovering (or focusing / tapping) a card flips it 3D-style,
 * revealing a slightly more detailed description on the back. Uses CSS
 * transform + perspective — no library. State is one selected index so
 * only one card can be "active" at a time (last-hovered wins).
 *
 * Rings + serif labels keep the whole thing feeling editorial rather than
 * corporate.
 */

const VALUES = [
  {
    index: "01",
    icon: "ri-leaf-line",
    title: "Presence",
    kicker: "Being with, not doing to.",
    back:
      "We put down the checklist first. Before we ask what to fix, we ask what it is like to be you right now.",
  },
  {
    index: "02",
    icon: "ri-shield-check-line",
    title: "Confidentiality",
    kicker: "Your room, sealed.",
    back:
      "Nothing shared in a session leaves the room. Our records are minimal, encrypted, and never used for marketing.",
  },
  {
    index: "03",
    icon: "ri-hand-heart-line",
    title: "Non-judgement",
    kicker: "Every version of you is welcome.",
    back:
      "There is no wrong reason to come, no story too small, and no feeling that will be met with a raised eyebrow here.",
  },
  {
    index: "04",
    icon: "ri-hourglass-2-line",
    title: "Unhurried Time",
    kicker: "Silence is not empty.",
    back:
      "We keep sessions to a single client per hour, with time before and after, so no one is rushed out the door.",
  },
];

export default function AboutValues() {
  const [flippedIndex, setFlippedIndex] = useState<number | null>(null);

  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const gridRef = useEnterAnimation<HTMLDivElement>({ delay: 300 });

  return (
    <section
      id="about-values"
      className="relative w-full bg-background-50"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            About Us / 03
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 03 · What we hold
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex flex-col items-center justify-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1400px] w-full mx-auto">
          <div ref={eyebrowRef} className="mb-8 flex items-center gap-3">
            <span
              className="font-heading"
              style={{
                fontSize: "clamp(2.75rem, 4vw, 4rem)",
                fontWeight: 300,
                letterSpacing: "-0.03em",
                color: "oklch(var(--foreground-950) / 0.35)",
                lineHeight: 1,
                fontVariationSettings: "'opsz' 144",
              }}
            >
              03
            </span>
            <span className="w-12 h-[1px] bg-foreground-950/25"></span>
            <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
              Our values
            </span>
          </div>

          <h2
            ref={titleRef}
            className="mb-16 max-w-[900px]"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2.5rem, 5vw, 5rem)",
              fontWeight: 300,
              letterSpacing: "-0.025em",
              lineHeight: 1.05,
              color: "oklch(var(--foreground-950))",
              fontVariationSettings: "'opsz' 144",
            }}
          >
            Four things we keep
            <br />
            <span className="italic text-foreground-950/85">
              in every session.
            </span>
          </h2>

          <p className="text-[10px] tracking-[0.3em] uppercase text-foreground-950/55 font-medium mb-6">
            Hover a card to read more
          </p>

          <div
            ref={gridRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
            style={{ perspective: "1400px" }}
          >
            {VALUES.map((v, i) => {
              const isFlipped = flippedIndex === i;
              return (
                <button
                  key={v.index}
                  type="button"
                  onMouseEnter={() => setFlippedIndex(i)}
                  onMouseLeave={() => setFlippedIndex(null)}
                  onFocus={() => setFlippedIndex(i)}
                  onBlur={() => setFlippedIndex(null)}
                  onClick={() =>
                    setFlippedIndex(isFlipped ? null : i)
                  }
                  aria-label={`${v.title} — ${v.kicker}`}
                  className="relative w-full text-left cursor-pointer bg-transparent border-0 p-0"
                  style={{
                    aspectRatio: "3 / 4",
                    transformStyle: "preserve-3d",
                  }}
                >
                  {/* Inner flipper */}
                  <div
                    className="relative w-full h-full transition-transform duration-700"
                    style={{
                      transformStyle: "preserve-3d",
                      transform: isFlipped
                        ? "rotateY(180deg)"
                        : "rotateY(0deg)",
                    }}
                  >
                    {/* FRONT */}
                    <div
                      className="absolute inset-0 rounded-2xl bg-background-100 border border-foreground-950/10 p-7 flex flex-col justify-between"
                      style={{
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className="uppercase font-medium text-foreground-950/50"
                          style={{
                            fontSize: "10px",
                            letterSpacing: "0.3em",
                          }}
                        >
                          Value · {v.index}
                        </span>
                        <span className="w-8 h-[1px] bg-foreground-950/25"></span>
                      </div>

                      <div className="flex flex-col gap-4">
                        <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500">
                          <i className={`${v.icon} text-2xl`}></i>
                        </span>
                        <h3
                          style={{
                            fontFamily: "var(--font-heading)",
                            fontSize: "clamp(1.5rem, 2vw, 2rem)",
                            fontWeight: 400,
                            letterSpacing: "-0.015em",
                            lineHeight: 1.1,
                            color: "oklch(var(--foreground-950))",
                          }}
                        >
                          {v.title}
                        </h3>
                        <p
                          className="italic"
                          style={{
                            fontFamily: "var(--font-heading)",
                            fontSize: "clamp(0.95rem, 1.05vw, 1.1rem)",
                            fontWeight: 300,
                            lineHeight: 1.5,
                            color: "oklch(var(--foreground-950) / 0.72)",
                          }}
                        >
                          {v.kicker}
                        </p>
                      </div>
                    </div>

                    {/* BACK */}
                    <div
                      className="absolute inset-0 rounded-2xl bg-foreground-950 text-background-50 p-7 flex flex-col justify-between"
                      style={{
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className="uppercase font-medium"
                          style={{
                            fontSize: "10px",
                            letterSpacing: "0.3em",
                            color: "rgba(255,255,255,0.7)",
                          }}
                        >
                          {v.title}
                        </span>
                        <span
                          className="uppercase font-medium"
                          style={{
                            fontSize: "10px",
                            letterSpacing: "0.3em",
                            color: "rgba(255,255,255,0.5)",
                          }}
                        >
                          {v.index}
                        </span>
                      </div>

                      <p
                        style={{
                          fontFamily: "var(--font-heading)",
                          fontSize: "clamp(1rem, 1.15vw, 1.2rem)",
                          fontWeight: 300,
                          lineHeight: 1.55,
                          color: "rgba(255,255,255,0.9)",
                          fontStyle: "italic",
                          letterSpacing: "0.005em",
                        }}
                      >
                        {v.back}
                      </p>

                      <div className="flex items-center gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                        <span
                          className="uppercase font-medium"
                          style={{
                            fontSize: "10px",
                            letterSpacing: "0.3em",
                            color: "rgba(255,255,255,0.65)",
                          }}
                        >
                          A quiet promise
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}